import { useEffect, useRef, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { ChevronDown, MapPin, Check } from "lucide-react";

const STORES = [
    {
        slug: "queens-long-island",
        prefix: "queens",
        short: "Queens",
        full: "Queens & Long Island",
        tagline: "Floral Park · serving NYC + LI",
    },
    {
        slug: "staten-island",
        prefix: "staten-island",
        short: "Staten Island",
        full: "Staten Island",
        tagline: "Tottenville · serving SI + NJ",
    },
];

const LS_KEY = "zen_selected_store_v1";

export function getSelectedStore() {
    try {
        const v = localStorage.getItem(LS_KEY);
        return STORES.find((s) => s.slug === v) || STORES[0];
    } catch {
        return STORES[0];
    }
}

// Header dropdown. Detects the store from the URL when on a store-scoped
// route; otherwise falls back to the localStorage preference. Selecting a
// store writes it to localStorage so non-store pages remember the choice.
export default function HeaderLocationPicker() {
    const { pathname, search } = useLocation();
    const [open, setOpen] = useState(false);
    const ref = useRef(null);

    // Detect current store from URL — slug like /menu/queens-long-island/...
    // or prefix like /queens, /staten-island. Falls back to LS preference.
    const detected = (() => {
        const parts = pathname.split("/").filter(Boolean);
        for (const s of STORES) {
            if (parts.includes(s.slug) || parts[0] === s.prefix) return s;
        }
        return null;
    })();
    const [stored, setStored] = useState(() => getSelectedStore());
    useEffect(() => {
        if (detected && detected.slug !== stored.slug) {
            Promise.resolve().then(() => setStored(detected));
            try {
                localStorage.setItem(LS_KEY, detected.slug);
            } catch (e) {
                if (typeof console !== "undefined") console.debug("storage:", e);
            }
        }
    }, [detected, stored.slug]);
    const current = detected || stored;

    // Click-away.
    useEffect(() => {
        if (!open) return;
        const onClick = (e) => {
            if (ref.current && !ref.current.contains(e.target)) setOpen(false);
        };
        document.addEventListener("mousedown", onClick);
        return () => document.removeEventListener("mousedown", onClick);
    }, [open]);

    // Where should clicking a store navigate? Preserve current page-type
    // when possible: /menu/<slug>/... and /brands/<slug> rewrite cleanly;
    // /<prefix> store-home pages flip to the other prefix; everything else
    // lands on the new store's home.
    const targetFor = (store) => {
        const re = /^(\/(?:menu|brands|locations))\/[^/]+(.*)$/;
        const m = pathname.match(re);
        if (m) return `${m[1]}/${store.slug}${m[2]}${search || ""}`;
        const parts = pathname.split("/").filter(Boolean);
        if (parts[0] === "queens" || parts[0] === "staten-island") {
            return `/${store.prefix}${parts.slice(1).length ? "/" + parts.slice(1).join("/") : ""}${search || ""}`;
        }
        return `/${store.prefix}`;
    };

    return (
        <div ref={ref} className="relative">
            <button
                onClick={() => setOpen((x) => !x)}
                aria-expanded={open}
                data-testid="header-location-picker"
                className="inline-flex items-center gap-2 px-3 py-2.5 border border-zen-line text-[11px] uppercase tracking-[0.22em] text-zen-ink hover:border-zen-olive transition-colors"
            >
                <MapPin size={12} className="text-zen-olive" />
                <span className="hidden lg:inline">{current.full}</span>
                <span className="lg:hidden">{current.short}</span>
                <ChevronDown
                    size={12}
                    className={`transition-transform ${open ? "rotate-180" : ""}`}
                />
            </button>
            {open && (
                <div
                    data-testid="header-location-menu"
                    className="absolute right-0 top-full mt-2 w-72 max-w-[calc(100vw-2rem)] bg-zen-bg border border-zen-line shadow-[0_18px_36px_rgba(26,31,18,0.12)] z-50"
                >
                    <p className="px-4 pt-4 pb-2 text-[10px] uppercase tracking-[0.28em] text-zen-ink-soft">
                        Choose your store
                    </p>
                    {STORES.map((s) => {
                        const active = s.slug === current.slug;
                        return (
                            <Link
                                key={s.slug}
                                to={targetFor(s)}
                                onClick={() => {
                                    try {
                                        localStorage.setItem(LS_KEY, s.slug);
                                    } catch (e) {
                                        if (typeof console !== "undefined")
                                            console.debug(
                                                "[zen-loc] localStorage write failed:",
                                                e?.name
                                            );
                                    }
                                    setOpen(false);
                                }}
                                data-testid={`header-location-${s.prefix}`}
                                className={`block px-4 py-3 hover:bg-zen-surface transition-colors border-t border-zen-line ${
                                    active ? "bg-zen-surface" : ""
                                }`}
                            >
                                <div className="flex items-start justify-between gap-3">
                                    <div>
                                        <p className="font-serif text-base text-zen-ink leading-tight">
                                            ZenZest {s.full}
                                        </p>
                                        <p className="text-[10px] uppercase tracking-[0.22em] text-zen-ink-soft mt-1">
                                            {s.tagline}
                                        </p>
                                    </div>
                                    {active && (
                                        <Check
                                            size={14}
                                            className="text-zen-olive shrink-0 mt-1"
                                        />
                                    )}
                                </div>
                            </Link>
                        );
                    })}
                </div>
            )}
        </div>
    );
}
